import { useCallback, useEffect, useRef, useState } from 'react'
import { Upload } from 'lucide-react'
import {
  type AdminAsset,
  type AssetFolder,
  bulkMoveAssets,
  createFolder,
  deleteAssetSafe,
  deleteFolderSafe,
  getFolders,
  getAssets,
  renameFolder,
  updateAsset,
  uploadAsset,
} from '../lib/api'
import { useAuth } from '../context/AuthContext'
import FolderSidebar from './FolderSidebar'
import AssetGrid from './AssetGrid'
import AssetDetailModal from './AssetDetailModal'
import MoveToFolderModal from './MoveToFolderModal'
import UploadPreviewModal from './UploadPreviewModal'

interface FileManagerPanelProps {
  mode: 'page' | 'picker'
  onSelect?: (asset: AdminAsset) => void
  feature?: string
}

export default function FileManagerPanel({ mode, onSelect, feature }: FileManagerPanelProps) {
  const { token } = useAuth()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const featureAutoSelected = useRef(false)

  const [folders, setFolders] = useState<AssetFolder[]>([])
  const [assets, setAssets] = useState<AdminAsset[]>([])
  const [activeFolderId, setActiveFolderId] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [pageSelectedIds, setPageSelectedIds] = useState<Set<string>>(new Set())
  const [pickerSelectedId, setPickerSelectedId] = useState<string | null>(null)
  const [moveTargetIds, setMoveTargetIds] = useState<string[] | null>(null)
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [uploadState, setUploadState] = useState<'idle' | 'uploading' | 'success' | 'error'>('idle')
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadErrorMessage, setUploadErrorMessage] = useState<string | undefined>()
  const [deletingFolder, setDeletingFolder] = useState<{ id: string } | null>(null)
  const [folderDeleteError, setFolderDeleteError] = useState<string | null>(null)
  const [assetDeleteError, setAssetDeleteError] = useState<string | null>(null)
  const [confirmingDeleteAssetId, setConfirmingDeleteAssetId] = useState<string | null>(null)
  const [confirmingBulkDelete, setConfirmingBulkDelete] = useState(false)

  const [detailAsset, setDetailAsset] = useState<AdminAsset | null>(null)

  const loadAll = useCallback(async () => {
    if (!token) return
    setLoading(true)
    setError(null)
    try {
      const [foldersData, assetsData] = await Promise.all([
        getFolders(token),
        getAssets(token),
      ])
      setFolders(foldersData)
      setAssets(assetsData)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal memuat assets')
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => { loadAll() }, [loadAll])

  useEffect(() => {
    if (!feature || !token || loading || featureAutoSelected.current) return
    featureAutoSelected.current = true
    const match = folders.find((f) => f.name === feature)
    if (match) { setActiveFolderId(match.id); return }
    createFolder(token, feature)
      .then((newFolder) => {
        setFolders((prev) => [...prev, newFolder].sort((a, b) => a.name.localeCompare(b.name)))
        setActiveFolderId(newFolder.id)
      })
      .catch(() => {})
  }, [feature, token, loading, folders])

  const visibleAssets =
    activeFolderId === null
      ? assets
      : activeFolderId === '__uncategorized__'
      ? assets.filter((a) => a.folder_id === null)
      : assets.filter((a) => a.folder_id === activeFolderId)

  // ── Folder operations ────────────────────────────────────────────────────────

  const handleFolderCreate = async (name: string) => {
    if (!token) return
    const folder = await createFolder(token, name)
    setFolders((prev) => [...prev, folder].sort((a, b) => a.name.localeCompare(b.name)))
  }

  const handleFolderRename = async (id: string, name: string) => {
    if (!token) return
    const updated = await renameFolder(token, id, name)
    setFolders((prev) =>
      prev.map((f) => f.id === id ? updated : f).sort((a, b) => a.name.localeCompare(b.name))
    )
  }

  const handleFolderDeleteRequest = (id: string, _fileCount: number) => {
    setFolderDeleteError(null)
    setDeletingFolder({ id })
  }

  const handleFolderDeleteConfirm = async () => {
    if (!token || !deletingFolder) return
    const result = await deleteFolderSafe(token, deletingFolder.id)
    if (!result.ok) {
      setFolderDeleteError(result.message)
      return
    }
    setFolders((prev) => prev.filter((f) => f.id !== deletingFolder.id))
    if (activeFolderId === deletingFolder.id) setActiveFolderId(null)
    setDeletingFolder(null)
    setFolderDeleteError(null)
  }

  // ── Asset operations ─────────────────────────────────────────────────────────

  const handleAssetDrop = async (assetId: string, targetFolderId: string | null) => {
    if (!token) return
    await updateAsset(token, assetId, { folder_id: targetFolderId })
    setAssets((prev) => prev.map((a) => a.id === assetId ? { ...a, folder_id: targetFolderId } : a))
    setPageSelectedIds((prev) => { const next = new Set(prev); next.delete(assetId); return next })
  }

  const handleMoveConfirm = async (targetFolderId: string | null) => {
    if (!token || !moveTargetIds) return
    await bulkMoveAssets(token, moveTargetIds, targetFolderId)
    setAssets((prev) =>
      prev.map((a) => moveTargetIds.includes(a.id) ? { ...a, folder_id: targetFolderId } : a)
    )
    if (detailAsset && moveTargetIds.includes(detailAsset.id)) {
      setDetailAsset((prev) => prev ? { ...prev, folder_id: targetFolderId } : null)
    }
    setPageSelectedIds(new Set())
    setMoveTargetIds(null)
  }

  const handleDeleteAsset = (assetId: string) => {
    setAssetDeleteError(null)
    setConfirmingDeleteAssetId(assetId)
  }

  const handleDeleteAssetConfirm = async () => {
    if (!token || !confirmingDeleteAssetId) return
    const assetId = confirmingDeleteAssetId
    setConfirmingDeleteAssetId(null)
    const result = await deleteAssetSafe(token, assetId)
    if (!result.ok) {
      setAssetDeleteError(result.message)
      return
    }
    setAssets((prev) => prev.filter((a) => a.id !== assetId))
    setPageSelectedIds((prev) => { const next = new Set(prev); next.delete(assetId); return next })
  }

  const handleBulkDelete = async () => {
    if (!token) return
    for (const id of Array.from(pageSelectedIds)) {
      if (!token) return
      const result = await deleteAssetSafe(token, id)
      if (!result.ok) { setAssetDeleteError(result.message); continue }
      setAssets((prev) => prev.filter((a) => a.id !== id))
      setPageSelectedIds((prev) => { const next = new Set(prev); next.delete(id); return next })
    }
    setPageSelectedIds(new Set())
  }

  // ── Asset detail modal ────────────────────────────────────────────────────────

  const handleOpenDetail = (asset: AdminAsset) => {
    setDetailAsset(asset)
  }

  const handleAssetUpdate = (updated: AdminAsset) => {
    setAssets((prev) => prev.map((a) => a.id === updated.id ? updated : a))
    setDetailAsset(updated)
  }

  const handleDeleteFromDetail = (assetId: string) => {
    setAssets((prev) => prev.filter((a) => a.id !== assetId))
    setPageSelectedIds((prev) => { const s = new Set(prev); s.delete(assetId); return s })
    setDetailAsset(null)
  }

  const handleMoveFromDetail = (assetId: string) => {
    setMoveTargetIds([assetId])
  }

  // ── Upload ───────────────────────────────────────────────────────────────────

  const effectiveFolderId = activeFolderId === '__uncategorized__' ? null : activeFolderId

  const handleUpload = async (file: File, opts: { filename: string; alt: string }) => {
    if (!token) return
    setUploadState('uploading')
    setUploadProgress(0)
    setUploadErrorMessage(undefined)
    try {
      const asset = await uploadAsset(
        token,
        file,
        { ...opts, folder_id: effectiveFolderId, feature },
        (p) => setUploadProgress(p),
      )
      setUploadState('success')
      setAssets((prev) => [asset, ...prev])
      if (mode === 'picker') setPickerSelectedId(asset.id)
      setTimeout(() => {
        setPendingFile(null)
        setUploadState('idle')
        setUploadProgress(0)
      }, 1500)
    } catch (e) {
      setUploadState('error')
      setUploadErrorMessage(e instanceof Error ? e.message : 'Upload gagal')
    }
  }

  const handleUploadRetry = () => {
    setUploadState('idle')
    setUploadProgress(0)
    setUploadErrorMessage(undefined)
  }

  const handleUploadClose = () => {
    if (uploadState === 'uploading') return
    setPendingFile(null)
    setUploadState('idle')
    setUploadProgress(0)
    setUploadErrorMessage(undefined)
  }

  const handleUploadFile = (file: File) => { setPendingFile(file) }
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) setPendingFile(file)
    e.target.value = ''
  }

  // ── Asset click ──────────────────────────────────────────────────────────────

  const handleAssetClick = (asset: AdminAsset) => {
    if (mode === 'picker') {
      setPickerSelectedId((prev) => prev === asset.id ? null : asset.id)
    }
  }

  const handlePickerConfirm = () => {
    const asset = assets.find((a) => a.id === pickerSelectedId)
    if (asset && onSelect) onSelect(asset)
  }

  const handleCopyUrl = (url: string) => {
    navigator.clipboard.writeText(url)
  }

  const activeFolder = folders.find((f) => f.id === activeFolderId)
  const folderLabel =
    activeFolderId === '__uncategorized__' ? 'Tanpa Folder' : activeFolder?.name
  const uploadLabel = folderLabel ? `Upload ke "${folderLabel}"` : 'Upload Baru'

  if (loading) {
    return <div className="flex items-center justify-center flex-1 text-zinc-500 text-sm p-8">Memuat assets...</div>
  }
  if (error) {
    return <div className="flex items-center justify-center flex-1 text-red-400 text-sm p-8">{error}</div>
  }

  return (
    <div className={`flex ${mode === 'page' ? 'h-full' : 'flex-1 min-h-0'}`}>
      {/* Left sidebar */}
      <FolderSidebar
        folders={folders}
        assets={assets}
        activeFolderId={activeFolderId}
        onFolderSelect={setActiveFolderId}
        onFolderCreate={handleFolderCreate}
        onFolderRename={handleFolderRename}
        onFolderDelete={handleFolderDeleteRequest}
        onAssetDrop={handleAssetDrop}
      />

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0 p-4 gap-3">
        {/* Asset delete error banner */}
        {assetDeleteError && (
          <div className="flex items-center gap-2 bg-red-900/30 border border-red-800 rounded-lg px-3 py-2 flex-shrink-0">
            <span className="text-xs text-red-300 flex-1">{assetDeleteError}</span>
            <button type="button" onClick={() => setAssetDeleteError(null)} className="text-red-500 hover:text-red-300 text-xs">✕</button>
          </div>
        )}

        {/* Toolbar row */}
        <div className="flex items-center justify-between flex-shrink-0">
          <p className="text-zinc-500 text-xs">
            {visibleAssets.length} {visibleAssets.length === 1 ? 'file' : 'files'}
            {folderLabel && <span className="text-zinc-600"> · dalam "{folderLabel}"</span>}
          </p>
          <button type="button"
            onClick={() => fileInputRef.current?.click()}
            className={`flex items-center gap-2 text-xs font-semibold px-4 py-2 rounded transition-colors ${
              mode === 'page'
                ? 'bg-yellow-500 hover:bg-yellow-400 text-black'
                : 'bg-zinc-700 hover:bg-zinc-600 text-white font-normal px-3 py-1.5'
            }`}
          >
            <Upload size={mode === 'page' ? 13 : 12} />
            {uploadLabel}
          </button>
        </div>

        {/* Bulk action toolbar (page mode only) */}
        {mode === 'page' && pageSelectedIds.size > 0 && (
          <div className="flex items-center gap-2 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 flex-shrink-0">
            <span className="text-xs text-zinc-300 flex-1">{pageSelectedIds.size} dipilih</span>
            <button type="button"
              onClick={() => setMoveTargetIds(Array.from(pageSelectedIds))}
              className="text-xs text-zinc-300 hover:text-white bg-zinc-700 hover:bg-zinc-600 px-2 py-1 rounded transition-colors"
            >
              Pindahkan
            </button>
            <button type="button"
              onClick={() => setConfirmingBulkDelete(true)}
              className="text-xs text-red-400 hover:text-red-300 bg-zinc-700 hover:bg-red-900/30 px-2 py-1 rounded transition-colors"
            >
              Hapus
            </button>
            <button type="button"
              onClick={() => setPageSelectedIds(new Set())}
              className="text-xs text-zinc-500 hover:text-white"
            >
              Batal pilih
            </button>
          </div>
        )}

        {/* Asset grid */}
        <AssetGrid
          assets={visibleAssets}
          mode={mode}
          pickerSelectedId={pickerSelectedId}
          pageSelectedIds={pageSelectedIds}
          onAssetClick={handleAssetClick}
          onOpenDetail={mode === 'page' ? handleOpenDetail : undefined}
          onUploadFile={handleUploadFile}
          onMoveRequest={(ids) => setMoveTargetIds(ids)}
          onDeleteRequest={handleDeleteAsset}
          onCopyUrl={handleCopyUrl}
          onSelectionChange={setPageSelectedIds}
        />

        {/* Picker confirm footer */}
        {mode === 'picker' && (
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-800 flex-shrink-0">
            <button type="button"
              disabled={!pickerSelectedId}
              onClick={handlePickerConfirm}
              className="text-sm bg-yellow-500 hover:bg-yellow-400 disabled:opacity-40 disabled:cursor-not-allowed text-black font-medium px-4 py-1.5 rounded transition-colors"
            >
              Pilih
            </button>
          </div>
        )}
      </div>

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        className="hidden"
        onChange={handleFileInputChange}
      />

      {/* Upload preview modal */}
      {pendingFile && (
        <UploadPreviewModal
          file={pendingFile}
          onConfirm={(opts) => handleUpload(pendingFile, opts)}
          onClose={handleUploadClose}
          uploadState={uploadState}
          uploadProgress={uploadProgress}
          errorMessage={uploadErrorMessage}
          onRetry={handleUploadRetry}
        />
      )}

      {/* Move to folder modal */}
      {moveTargetIds && (
        <MoveToFolderModal
          folders={folders}
          assetCount={moveTargetIds.length}
          onConfirm={handleMoveConfirm}
          onClose={() => setMoveTargetIds(null)}
        />
      )}

      {/* Asset detail modal (page mode only) */}
      {detailAsset && mode === 'page' && (
        <AssetDetailModal
          asset={detailAsset}
          folders={folders}
          token={token!}
          onClose={() => setDetailAsset(null)}
          onUpdate={handleAssetUpdate}
          onDelete={handleDeleteFromDetail}
          onMoveRequest={handleMoveFromDetail}
        />
      )}

      {/* Bulk delete confirmation */}
      {confirmingBulkDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/70" onClick={() => setConfirmingBulkDelete(false)} />
          <div className="relative z-10 bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-xs mx-4 p-5 space-y-4">
            <p className="text-sm text-white">Hapus {pageSelectedIds.size} file yang dipilih?</p>
            <p className="text-xs text-zinc-400">Tindakan ini tidak bisa dibatalkan.</p>
            <div className="flex gap-2 justify-end">
              <button type="button"
                onClick={() => setConfirmingBulkDelete(false)}
                className="text-xs text-zinc-400 hover:text-white bg-zinc-700 hover:bg-zinc-600 px-3 py-1.5 rounded transition-colors"
              >
                Batal
              </button>
              <button type="button"
                onClick={() => { setConfirmingBulkDelete(false); handleBulkDelete() }}
                className="text-xs text-white bg-red-600 hover:bg-red-500 px-3 py-1.5 rounded transition-colors"
              >
                Hapus
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Asset delete confirmation */}
      {confirmingDeleteAssetId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/70" onClick={() => setConfirmingDeleteAssetId(null)} />
          <div className="relative z-10 bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-xs mx-4 p-5 space-y-4">
            <p className="text-sm text-white">Hapus asset ini?</p>
            <p className="text-xs text-zinc-400">File akan dihapus permanen dari storage.</p>
            <div className="flex gap-2 justify-end">
              <button type="button"
                onClick={() => setConfirmingDeleteAssetId(null)}
                className="text-xs text-zinc-400 hover:text-white bg-zinc-700 hover:bg-zinc-600 px-3 py-1.5 rounded transition-colors"
              >
                Batal
              </button>
              <button type="button"
                onClick={handleDeleteAssetConfirm}
                className="text-xs text-white bg-red-600 hover:bg-red-500 px-3 py-1.5 rounded transition-colors"
              >
                Hapus
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Folder delete confirmation */}
      {deletingFolder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/70" onClick={() => { setDeletingFolder(null); setFolderDeleteError(null) }} />
          <div className="relative z-10 bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-xs mx-4 p-5 space-y-4">
            <p className="text-sm text-white">Hapus folder ini?</p>
            {folderDeleteError && (
              <p className="text-xs text-red-400">{folderDeleteError}</p>
            )}
            <div className="flex gap-2 justify-end">
              <button type="button"
                onClick={() => { setDeletingFolder(null); setFolderDeleteError(null) }}
                className="text-xs text-zinc-400 hover:text-white bg-zinc-700 hover:bg-zinc-600 px-3 py-1.5 rounded transition-colors"
              >
                Batal
              </button>
              <button type="button"
                onClick={handleFolderDeleteConfirm}
                className="text-xs text-white bg-red-600 hover:bg-red-500 px-3 py-1.5 rounded transition-colors"
              >
                Hapus
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
